import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { ArrowLeft, Inbox } from "lucide-react";
import {
  filterGroupSchema,
  parseFilterFromSearchParam,
  type FilterTree,
} from "@colab/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import {
  ActiveViewColumnsButton,
  AddFilterMenu,
  SavedViewTabs,
} from "@/components/shared/saved-views";
import { useViews } from "@/features/admin/views/use-views";
import { useActiveView } from "@/features/admin/views/active-view";
import { Kanban } from "./kanban";
import { DealDrawer } from "./deal-drawer";
import { useAdminDeals } from "./use-admin-deals";

// Phase 7 sub-bucket 16 — admin deals Kanban + drawer page. Ports the
// /app/admin/deals/page.tsx server component into the SPA. URL state:
//
//   - `?showClosed=1`     → include the closed-won / closed-lost columns
//   - `?deal=<hubspotId>` → open the deal drawer (drawer-as-shareable-route)
//   - `?view=<id>`        → active Saved View (sub-bucket 18)
//   - `?filter=<base64>`  → ad-hoc FilterTree override (sub-bucket 18)
//
// HubSpot is the source of truth for stage transitions — no
// drag-between-columns affordance. Status changes happen in HubSpot and
// reverse-sync into the snapshot tables on the next webhook tick.

const ENTITY_TYPE = "admin_deal" as const;

export function DealsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const showClosed = searchParams.get("showClosed") === "1";
  const selectedDealId = searchParams.get("deal");

  // Saved Views resolution (sub-bucket 18 — first admin_deal consumer).
  const viewsQuery = useViews(ENTITY_TYPE);
  const active = useActiveView(viewsQuery.data);
  const urlFilter = useMemo<FilterTree | null>(() => {
    const raw = searchParams.get("filter");
    if (!raw) return null;
    let decoded: string | null = null;
    try {
      decoded = atob(raw);
    } catch {
      decoded = raw;
    }
    const parsed = parseFilterFromSearchParam(decoded);
    if (parsed === null) return null;
    const validated = filterGroupSchema.safeParse(parsed);
    return validated.success ? (validated.data as FilterTree) : null;
  }, [searchParams]);

  const effectiveFilter = urlFilter ?? active.filter;
  const filterIsOverride = urlFilter !== null && active.filter !== null;

  const query = useAdminDeals(showClosed, effectiveFilter);

  function toggleShowClosed() {
    const next = new URLSearchParams(searchParams);
    if (showClosed) {
      next.delete("showClosed");
    } else {
      next.set("showClosed", "1");
    }
    setSearchParams(next, { replace: true });
  }

  function selectDeal(hubspotDealId: string | null) {
    const next = new URLSearchParams(searchParams);
    if (hubspotDealId) {
      next.set("deal", hubspotDealId);
    } else {
      next.delete("deal");
    }
    setSearchParams(next, { replace: true });
  }

  function setFilterInUrl(tree: FilterTree | null) {
    const next = new URLSearchParams(searchParams);
    if (tree) {
      next.set("filter", btoa(JSON.stringify(tree)));
    } else {
      next.delete("filter");
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Deals
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            HubSpot Sales-pipeline deals associated with partners. Read-only —
            HubSpot is the source of truth.
          </p>
        </div>
        <button
          type="button"
          onClick={toggleShowClosed}
          className="rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
        >
          {showClosed ? "Hide closed" : "Show closed"}
        </button>
      </header>

      <SavedViewTabs
        entityType={ENTITY_TYPE}
        saveInitialState={{
          filter: effectiveFilter,
          columns: active.columns,
          sort: active.sort,
        }}
      />

      <div className="mb-4 flex items-center justify-end gap-2">
        <AddFilterMenu
          entityType={ENTITY_TYPE}
          value={effectiveFilter}
          onApply={setFilterInUrl}
          isOverride={filterIsOverride}
        />
        <ActiveViewColumnsButton
          entityType={ENTITY_TYPE}
          activeView={active.view}
        />
      </div>

      {query.isLoading ? (
        <KanbanSkeleton />
      ) : query.isError ? (
        <EmptyState
          icon={ArrowLeft}
          title="Couldn't load deals"
          description={query.error?.message ?? "Try refreshing."}
        />
      ) : !query.data || query.data.stages.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No HubSpot pipeline stages"
          description="Check the HUBSPOT_ACCESS_TOKEN on the worker so the Sales pipeline stages can be fetched."
        />
      ) : (
        <Kanban
          stages={query.data.stages}
          grouped={query.data.grouped}
          onSelectDeal={(id) => selectDeal(id)}
        />
      )}

      <DealDrawer
        hubspotDealId={selectedDealId}
        onClose={() => selectDeal(null)}
      />
    </div>
  );
}

function KanbanSkeleton() {
  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex w-[300px] shrink-0 flex-col rounded-xl border bg-card/40 p-2.5"
        >
          <Skeleton className="mb-3 h-4 w-24" />
          <div className="space-y-2">
            <Skeleton className="h-24 w-full rounded-md" />
            <Skeleton className="h-24 w-full rounded-md" />
            <Skeleton className="h-24 w-full rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
}
